import { useState, useEffect, useCallback, useRef } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/evo/ui/select';
import { Input } from '@/components/evo/ui/input';
import useGetCustomersLookUp from '@/pages/ContractDetails/ContractParties/hooks/useGetCustomersLookUp.query';
import { CustomerLookupOutput } from '@/graphql/contracts/_generated_/contracts';

interface CustomerSelectorProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export function CustomerSelector({ value, onChange, className }: CustomerSelectorProps) {
  const { getCustomersLookUp, customers, isCustomersLookUpLoading } = useGetCustomersLookUp();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerLookupOutput | null>(null);
  const initialValueRef = useRef<string>(value);
  const initialCustomerRef = useRef<CustomerLookupOutput | null>(null);

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    if (term.length >= 2) {
      getCustomersLookUp(term);
    }
  }, [getCustomersLookUp]);

  useEffect(() => {
    if (value && customers) {
      const found = customers.find((c) => c?.customerAccountNumber === value);
      if (found) {
        setSelectedCustomer(found);
        if (value === initialValueRef.current && !initialCustomerRef.current) {
          initialCustomerRef.current = found;
        }
      }
    }
  }, [value, customers]);

  const matchedCustomer = selectedCustomer?.customerAccountNumber === value ? selectedCustomer : null;
  
  const displayValue = value
    ? matchedCustomer
      ? `[${matchedCustomer.customerAccountNumber}] - ${matchedCustomer.customerName}`
      : value === initialValueRef.current && initialCustomerRef.current
        ? `[${initialCustomerRef.current.customerAccountNumber}] - ${initialCustomerRef.current.customerName}`
        : value
    : '';

  const handleValueChange = (newValue: string) => {
    const customer = customers?.find((c) => c?.customerAccountNumber === newValue);
    if (customer) {
      setSelectedCustomer(customer);
    }
    onChange(newValue);
  };

  return (
    <div className="space-y-2">
      <Select value={value} onValueChange={handleValueChange}>
        <SelectTrigger className={className}>
          <SelectValue placeholder={isCustomersLookUpLoading ? 'Loading...' : 'Select customer'}>
            {displayValue}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <div className="p-2">
            <Input
              placeholder="Type to search customers (min 2 chars)..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="h-8"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          {isCustomersLookUpLoading ? (
            <div className="p-2 text-sm text-gray-500 text-center">Loading...</div>
          ) : !customers || customers.length === 0 ? (
            <div className="p-2 text-sm text-gray-500 text-center">
              {searchTerm.length < 2 ? 'Type at least 2 characters to search' : 'No customers found'}
            </div>
          ) : (
            customers.map((customer) => (
              customer && (
                <SelectItem key={customer.customerAccountNumber} value={customer.customerAccountNumber || ''}>
                  [{customer.customerAccountNumber}] - {customer.customerName}
                </SelectItem>
              )
            ))
          )}
        </SelectContent>
      </Select>
    </div>
  );
}
